import { Activity, Edit, History, Pause, Play, RefreshCw } from 'lucide-react'
import { JobDefinicao, TipoExecucaoJobRotulo, TipoExecucaoJobValores } from '../tipos/job'
import { useAlternarAtivo, useExecutarAgora } from '../hooks/useJobs'

interface TabelaJobsProps {
  jobs: JobDefinicao[]
  onRefresh: () => void
  onEditar: (job: JobDefinicao) => void
  onVerHistorico: (job: JobDefinicao) => void
}

const formatarData = (valor?: string | null) => {
  if (!valor) return '-'
  const data = new Date(valor)
  if (Number.isNaN(data.getTime())) return '-'
  return data.toLocaleString('pt-BR')
}

const formatarAgendamento = (job: JobDefinicao) => {
  if (job.tipoExecucaoCodigo === TipoExecucaoJobValores.Diaria)
    return `Diariamente às ${job.horarioDiario ?? '--:--'} (UTC)`
  if (job.tipoExecucaoCodigo === TipoExecucaoJobValores.Intervalo)
    return `A cada ${job.intervaloMinutos ?? 0} min`
  return 'Apenas manual'
}

export function TabelaJobs({ jobs, onRefresh, onEditar, onVerHistorico }: TabelaJobsProps) {
  const alternar = useAlternarAtivo()
  const executar = useExecutarAgora()

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Jobs do sistema</h3>
            <p className="text-sm text-gray-600">
              {jobs.length} job(s) parametrizado(s)
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
          title="Atualizar"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agendamento</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Última execução</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Próxima execução</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {jobs.map((job) => (
              <tr key={job.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{job.nome}</div>
                  <div className="text-xs text-gray-500">{job.codigo}</div>
                  {job.descricao && (
                    <div className="text-xs text-gray-500 mt-1 max-w-md">{job.descricao}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {TipoExecucaoJobRotulo[job.tipoExecucaoCodigo]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {formatarAgendamento(job)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {formatarData(job.ultimaExecucaoFimSucesso ?? job.ultimaExecucaoInicio)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {formatarData(job.proximaExecucaoEstimada)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      job.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {job.ativo ? 'Ativo' : 'Pausado'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => alternar.mutate({ id: job.id, ativo: !job.ativo })}
                      disabled={alternar.isLoading}
                      className="p-2 text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-all"
                      title={job.ativo ? 'Pausar' : 'Retomar'}
                    >
                      {job.ativo ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => executar.mutate(job.id)}
                      disabled={executar.isLoading}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      title="Executar agora"
                    >
                      <Play className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onEditar(job)}
                      className="p-2 text-gray-600 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-all"
                      title="Editar parametrização"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onVerHistorico(job)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      title="Histórico"
                    >
                      <History className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
